import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import PayActivityChallanModal from "./PayActivityChallanModal";
import FullScreenLoader from "../../components/FullScreenLoader";
import EnrollmentsModal from "./EnrollmentsModal";

function ActivityCreationAndFeedbackForm() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [contractsData, setContractsData] = useState([]);

  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const [customDropdownName, setCustomDropdownName] = useState("");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      activityCategoryId: null,
      description: "",
      objective: "",
      centerId: null,
      activityVenueId: null,
      scheduleDateTime: null,
      activityFee: null,
      maxCapicity: null,
      contractId: null,
      activitySponsorId: null,
      sponsorRatio: null,
      termsAndConditions: "",
      activityEndDate: null,
      activityRegDeadline: null,
      vat: 0,
      totalAmount: 0,
      filePath: "",
      fileName: "",
      base64String: "",
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      campusActivityClassSectionDtos: [
        {
          id: 0,
          classSectionId: null,
        },
      ],
      campusActivitySlotsDtos: [
        {
          id: 0,
          name: "",
          slotStartTime: "",
          slotEndTime: "",
        },
      ],
    },
  });

  const fetchCenters = async () => {
    try {
      const response = await get(
        `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCentersData(response);

      if (savedCenterId) {
        const selectedCenter = response.find(
          (center) => center.id === parseInt(savedCenterId, 10)
        );
        if (selectedCenter) {
          setValue("centerId", {
            value: selectedCenter.id,
            label: selectedCenter.name,
          });
          setCenterId(selectedCenter.id);
          fetchClassSectionsByCenterId(selectedCenter.id);
        }
      }
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const getContractsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(`CampusContract/GetAllContracts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setContractsData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    fetchCenters();
    getContractsList();
  }, []);

  const [activityCategoryData, setActivityCategoryData] = useState([]);
  const [activityVenueData, setActivityVenueData] = useState([]);
  const [activitySponsorData, setActivitySponsorData] = useState([]);

  const [showPayActivityChallanModal, setShowPayActivityChallanModal] =
    useState(false);
  const [payRegModalData, setPayRegModalData] = useState(null);

  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false);
  const [enrollmentsData, setEnrollmentsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("activityCategoryId", setActivityCategoryData);
    fetchCommonSettings("activityVenueId", setActivityVenueData);
    fetchCommonSettings("activitySponsorId", setActivitySponsorData);
  }, [token]);

  const activityFee = watch("activityFee");
  const selectedSponsorId = watch("activitySponsorId");

  useEffect(() => {
    if (
      activityFee === null ||
      activityFee === undefined ||
      activityFee === ""
    ) {
      setValue("totalAmount", "");
    } else if (!isNaN(activityFee)) {
      const vatAmount = activityFee * 0.17;
      const totalAmount = activityFee + vatAmount;
      setValue("totalAmount", parseFloat(totalAmount.toFixed(2)));
    } else {
      setValue("totalAmount", "");
    }
  }, [activityFee, setValue]);

  const [activitiesData, setActivitiesData] = useState([]);

  const getActivitiesList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetActivities?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActivitiesData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getActivitiesList();
  }, [triggerRerender]);

  const handleEdit = async (activityId) => {
    try {
      const response = await get(
        `CampusActivity/GetActivityById?activityId=${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const activity = response?.campusActivityDto;

      const classSectionsResponse = await get(
        `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}&userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Map class section options into label/value format for react-select
      const classSectionOptions = classSectionsResponse.map((section) => ({
        value: section.id,
        label: section.name,
      }));

      // Map selected sections from API into { classSectionId } format used by form
      const selectedClassSections = activity.campusActivityClassSectionDtos.map(
        (item) => ({
          classSectionId: item.classSectionId,
        })
      );

      const selectedContract = contractsData.find(
        (contract) => contract.id === activity.contractId
      );

      const slots = activity.campusActivitySlotsDtos.map((slot) => ({
        ...slot,
      }));

      const imageUrl = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${activity.filePath}`;

      setPreviewUrl(imageUrl);
      setEventFileName(activity.fileName);

      reset({
        ...activity,
        centerId: getValues("centerId"),
        contractId: {
          value: selectedContract?.id || "",
          label: selectedContract?.name || "",
        },
        updatedBy: localStorage.getItem("userId"),
        scheduleDateTime: activity.scheduleDateTime.split("T")[0],
        activityEndDate: activity.activityEndDate.split("T")[0],
        activityRegDeadline: activity.activityRegDeadline.split("T")[0],
        campusActivityClassSectionDtos: selectedClassSections,
        campusActivitySlotsDtos: slots,
      });
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this record?"
    );

    if (confirmDelete) {
      try {
        const response = await post(
          `CampusActivity/RemoveActivityById?id=${rowId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Slot",
      selector: (row) => row.slot,
      sortable: true,
      // width: "150px",
    },
    {
      name: "Activity Name",
      selector: (row) => row.activityName,
      sortable: true,
      // width: "150px",
    },
    {
      name: "Date",
      selector: (row) => row.scheduleDateTime.split("T")[0],
      sortable: true,
      width: "150px",
    },
    {
      name: "Max Capacity",
      selector: (row) => row.maxCapicity,
      sortable: true,
      // width: "150px",
    },
    {
      name: "Enrollments",
      selector: (row) => (
        <button
          type="button"
          className="btn btn-warning px-2 py-1 fs-10"
          onClick={() => handleEnrollmentsClick(row.slotId)}
        >
          {row.enrollment}
        </button>
      ),
      sortable: true,
      // width: "150px",
    },
    {
      name: "Remaining",
      selector: (row) => row.remaning,
      sortable: true,
      // width: "150px",
    },

    {
      name: "Action",
      width: "80px",
      selector: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Edit"
            onClick={() => handleEdit(row.activityId)}
          >
            <i className="fas fa-pencil"></i>
          </button>
          <button
            type="button"
            className="btn p-0"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Delete"
            onClick={() => handleDelete(row.id)}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const fetchClassSectionsByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}&userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassSectionsData(response);
      } else {
        setClassSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  const [eventFileName, setEventFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setEventFileName(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("base64String", base64String, { shouldValidate: true });
        setPreviewUrl(reader.result);
        trigger("fileName");
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "campusActivitySlotsDtos",
  });

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const campusActivityClassSectionDtos =
      data.campusActivityClassSectionDtos.map((classSection) => ({
        id: 0,
        classSectionId: classSection.classSectionId,
      }));

    const formData = {
      ...data,
      centerId: data?.centerId?.value,
      contractId: data?.contractId?.value,
      campusActivityClassSectionDtos: campusActivityClassSectionDtos,
      fileName: eventFileName,
    };

    try {
      const response = await post("CampusActivity/UpdateActivites", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success("Success!");
        setTriggerRerender((prev) => !prev);
        setPreviewUrl(null);
        reset({
          id: 0,
          name: "",
          activityCategoryId: null,
          description: "",
          objective: "",
          centerId: getValues("centerId"),
          activityVenueId: null,
          scheduleDateTime: null,
          activityFee: null,
          maxCapicity: null,
          contractId: null,
          activitySponsorId: null,
          sponsorRatio: null,
          termsAndConditions: "",
          activityEndDate: null,
          activityRegDeadline: null,
          vat: 0,
          totalAmount: 0,
          filePath: "",
          fileName: "",
          base64String: "",
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
          campusActivityClassSectionDtos: [
            {
              id: 0,
              classSectionId: null,
            },
          ],
          campusActivitySlotsDtos: [
            {
              id: 0,
              name: "",
              slotStartTime: "",
              slotEndTime: "",
            },
          ],
        });
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchEvents = (fetchInfo, successCallback, failureCallback) => {
    get(`CampusActivity/GetActivities?centerId=${centerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (Array.isArray(response)) {
          const allEvents = [];

          response.forEach((activity) => {
            const date = activity.scheduleDateTime.split("T")[0];

            allEvents.push({
              id: activity.slotId,
              title: `${activity.activityName} (${activity.slotStartTime} - ${activity.slotEndTime})`,
              start: `${date}T${activity.slotStartTime}:00`,
              end: `${date}T${activity.slotEndTime}:00`,
              extendedProps: {
                activityName: activity.activityName,
                maxCapicity: activity.maxCapicity,
                enrollment: activity.enrollment,
                remaining: activity.remaning,
                feePaid: activity.feePaid,
              },
            });
          });

          successCallback(allEvents);
        } else {
          failureCallback(new Error("Unexpected response format"));
        }
      })
      .catch((error) => {
        console.error("Error fetching events:", error);
        failureCallback(error);
      });
  };

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    const startDate = event.start.toISOString().split("T")[0];

    const formatTime = (dateObj) =>
      new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(dateObj);

    const startTime = event.start ? formatTime(event.start) : "";
    const endTime = event.end ? formatTime(event.end) : startTime;

    setSelectedEvent({
      activityName: event.extendedProps.activityName,
      date: startDate,
      time: `${startTime} - ${endTime}`,
      maxCapicity: event.extendedProps.maxCapicity,
      enrollment: event.extendedProps.enrollment,
      remaining: event.extendedProps.remaining,
      feePaid: event.extendedProps.feePaid,
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handlePayActivityChallanClick = async (row) => {
    try {
      const response = await get(
        `AssessmentType/GetRegistartionVoucherByRegIdAsync?studentFirstRegistrationId=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setPayRegModalData(response?.registrationVoucher);
        setShowPayActivityChallanModal(true);
      } else {
        console.error("Error fetching fee details");
      }
    } catch (error) {
      console.error("Error fetching admission voucher:", error);
    }
  };

  const submitPayActivityChallan = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const currentDate = new Date();
    const dateString = currentDate.toISOString();

    const formDataWithPaymentInfo = {
      ...formData,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      paidBy: localStorage.getItem("userId"),
      isPaid: true,
      paymentDate: dateString,
      id: formData.id,
      voucherNo: formData.voucherNo,
    };

    try {
      const response = await post(
        `AssessmentType/UpdateRegistartionVoucherPaidAsync`,
        formDataWithPaymentInfo,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setShowPayActivityChallanModal(false);
        setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const handleEnrollmentsClick = async (slotId) => {
    try {
      const response = await get(
        `CampusActivity/GetCampusActivityByCenterAndSlotId?CenterId=${centerId}&SlotId=${slotId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response) {
        setShowEnrollmentsModal(true);
        setEnrollmentsData(response);
      } else {
        console.error("Error fetching fee details");
      }
    } catch (error) {
      console.error("Error fetching admission voucher:", error);
    }
  };

  return (
    <>
      {/* <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Activity Creation</h2>
      </div> */}
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h4 className="mb-3 fs-8">Activity Creation</h4>
              <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                {/* <div className="col-md-4">
                  <label className="form-label">
                    Center
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerId", null);
                            setCenterId(null);
                          } else {
                            setValue("centerId", selectedOption);
                            setCenterId(selectedOption.value);
                            fetchClassSectionsByCenterId(selectedOption.value);
                          }
                          setClassSectionsData([]);
                          setValue("classSectionId", null);
                          setSelectedClassSectionId(null);

                          trigger("centerId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div> */}
                <div className="col-md-4">
                  <label className="form-label">
                    Activity Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-8">
                  <label className="form-label">
                    Year Group - Section
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="campusActivityClassSectionDtos"
                    control={control}
                    rules={{
                      required: "This field is required.",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSectionsData.map((classSection) => ({
                          value: classSection.id,
                          label: classSection.name,
                        }))}
                        onChange={(selectedOptions, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("campusActivityClassSectionDtos", []);
                          } else {
                            setValue(
                              "campusActivityClassSectionDtos",
                              selectedOptions
                            );
                            const mapped = selectedOptions.map((item) => ({
                              classSectionId: item.value,
                            }));
                            setValue("campusActivityClassSectionDtos", mapped);
                          }
                          trigger("campusActivityClassSectionDtos");
                        }}
                        isMulti
                        isClearable
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        value={
                          field.value?.map((item) => {
                            const match = classSectionsData.find(
                              (cs) => cs.id === item.classSectionId
                            );
                            return match
                              ? { value: match.id, label: match.name }
                              : null;
                          }) || []
                        }
                      />
                    )}
                  />
                  {errors.campusActivityClassSectionDtos && (
                    <small className="text-danger">
                      {errors.campusActivityClassSectionDtos.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Category<span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <div className="w-100">
                      <Controller
                        name="activityCategoryId"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            className="react-select-style"
                            classNamePrefix="react-select-style-pref"
                            options={activityCategoryData.map((item) => ({
                              value: item.id,
                              label: item.value,
                            }))}
                            isClearable
                            onChange={(selectedOption) =>
                              field.onChange(
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            value={
                              activityCategoryData
                                .map((item) => ({
                                  value: item.id,
                                  label: item.value,
                                }))
                                .find(
                                  (option) => option.value === field.value
                                ) || null
                            }
                          />
                        )}
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        style={{
                          padding: "5.5px 10px",
                        }}
                        onClick={() =>
                          handleCustomDropdownSetting("activityCategoryId")
                        }
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                  {errors.activityCategoryId && (
                    <small className="text-danger">
                      {errors.activityCategoryId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Venue/Location<span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <div className="w-100">
                      <Controller
                        name="activityVenueId"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            className="react-select-style"
                            classNamePrefix="react-select-style-pref"
                            options={activityVenueData.map((item) => ({
                              value: item.id,
                              label: item.value,
                            }))}
                            isClearable
                            onChange={(selectedOption) =>
                              field.onChange(
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            value={
                              activityVenueData
                                .map((item) => ({
                                  value: item.id,
                                  label: item.value,
                                }))
                                .find(
                                  (option) => option.value === field.value
                                ) || null
                            }
                          />
                        )}
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        style={{
                          padding: "5.5px 10px",
                        }}
                        onClick={() =>
                          handleCustomDropdownSetting("activityVenueId")
                        }
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                  {errors.activityVenueId && (
                    <small className="text-danger">
                      {errors.activityVenueId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Contract
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="contractId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={contractsData.map((contract) => ({
                          value: contract.id,
                          label: contract.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("contractId", null);
                          } else {
                            setValue("contractId", selectedOption);
                          }
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.contractId && (
                    <small className="text-danger">
                      {errors.contractId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Objective<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`objective`, {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                  />
                  {errors.objective && (
                    <small className="text-danger">
                      {errors.objective.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Fee Applicable<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("activityFee", {
                      valueAsNumber: true,
                      required: "This field is required.",
                      validate: (value) =>
                        value >= 0 || "Please enter a correct value.",
                    })}
                    type="number"
                    className="form-control"
                    onKeyDown={(e) => {
                      if (["-", "e", "+", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value < 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.activityFee && (
                    <small className="text-danger">
                      {errors.activityFee.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Total Amount VAT (17%)</label>
                  <input
                    {...register("totalAmount", {
                      valueAsNumber: true,
                      validate: (value) =>
                        value >= 0 || "Please enter a correct value.",
                    })}
                    type="number"
                    className="form-control"
                    disabled
                  />
                  {errors.totalAmount && (
                    <small className="text-danger">
                      {errors.totalAmount.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Maximum participant capacity
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`maxCapicity`, {
                      valueAsNumber: true,
                      required: "This field is required.",
                      validate: (value) =>
                        value >= 25 || "Min. 25 participants requied.",
                    })}
                    type="number"
                    className="form-control"
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value < 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.maxCapicity && (
                    <small className="text-danger">
                      {errors.maxCapicity.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Activity Sponsor<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="activitySponsorId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        options={activitySponsorData.map((item) => ({
                          value: item.id,
                          label: item.value,
                        }))}
                        onChange={(selectedOption) =>
                          field.onChange(
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        value={
                          activitySponsorData
                            .map((item) => ({
                              value: item.id,
                              label: item.value,
                            }))
                            .find((option) => option.value === field.value) ||
                          null
                        }
                      />
                    )}
                  />
                  {errors.activitySponsorId && (
                    <small className="text-danger">
                      {errors.activitySponsorId.message}
                    </small>
                  )}
                </div>

                {/* 👇 Ratio field controlled by selected Activity Sponsor */}
                {activitySponsorData.find(
                  (item) => item.id === selectedSponsorId
                )?.value === "External" && (
                  <div className="col-md-4">
                    <label className="form-label">
                      Ratio<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("sponsorRatio", {
                        valueAsNumber: true,
                        required: "This field is required.",
                        validate: (value) =>
                          value >= 0 || "Please enter correct value.",
                      })}
                      type="number"
                      className="form-control"
                      onKeyDown={(e) => {
                        if (["-", "e", "+", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        e.target.value =
                          e.target.value < 0 ? 1 : e.target.value;
                      }}
                    />
                    {errors.sponsorRatio && (
                      <small className="text-danger">
                        {errors.sponsorRatio.message}
                      </small>
                    )}
                  </div>
                )}

                <div className="m-0"></div>
                <div className="col-md-4">
                  <label className="form-label">
                    Attachment
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="image-uploader-wrapper-2 d-flex flex-column align-items-center">
                    <div className="image-preview d-flex justify-content-center align-items-center flex-shrink-0 mb-2 mt-1">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                      ) : (
                        <div>
                          <span className="fas fa-image fs-3 text-light"></span>
                        </div>
                      )}
                    </div>
                    <input
                      {...register("fileName", {
                        required: !previewUrl
                          ? "This field is required."
                          : false,
                      })}
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.fileName && (
                    <small className="text-danger">
                      {errors.fileName.message}
                    </small>
                  )}
                </div>
                <div className="m-0"></div>
                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <textarea
                    {...register("description", {})}
                    className="form-control"
                  ></textarea>
                  {errors.description && (
                    <small className="text-danger">
                      {errors.description.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Terms & Conditions
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <textarea
                    {...register("termsAndConditions", {})}
                    className="form-control"
                  ></textarea>
                  {errors.termsAndConditions && (
                    <small className="text-danger">
                      {errors.termsAndConditions.message}
                    </small>
                  )}
                </div>

                <div className="m-0"></div>

                <div className="col-md-4">
                  <label className="form-label">
                    Activity Start Date
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("scheduleDateTime", {
                      required: "Start Date is required.",
                    })}
                    type="date"
                    className="form-control"
                  />
                  {errors.scheduleDateTime && (
                    <small className="text-danger">
                      {errors.scheduleDateTime.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Activity End Date
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("activityEndDate", {
                      required: "End Date is required.",
                      validate: (value) =>
                        !watch("scheduleDateTime") ||
                        new Date(value) >=
                          new Date(watch("scheduleDateTime")) ||
                        "Must be after Start Date.",
                    })}
                    type="date"
                    className="form-control"
                  />
                  {errors.activityEndDate && (
                    <small className="text-danger">
                      {errors.activityEndDate.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Activity Registration Deadline
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("activityRegDeadline", {
                      required: "Registration Deadline is required.",
                      validate: (value) =>
                        !watch("scheduleDateTime") ||
                        new Date(value) >=
                          new Date(watch("scheduleDateTime")) ||
                        "Must be after Start Date.",
                    })}
                    type="date"
                    className="form-control"
                  />
                  {errors.activityRegDeadline && (
                    <small className="text-danger">
                      {errors.activityRegDeadline.message}
                    </small>
                  )}
                </div>

                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        Slot Name <span className="text-danger fs-9">*</span>
                      </label>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">
                        Start Time <span className="text-danger fs-9">*</span>
                      </label>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">
                        End Time <span className="text-danger fs-9">*</span>
                      </label>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Action</label>
                    </div>
                  </div>

                  {fields.map((field, index) => (
                    <div className="row g-3 mb-2" key={field.id}>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          {...register(
                            `campusActivitySlotsDtos.${index}.name`,
                            {
                              required: "This field is required.",
                            }
                          )}
                        />
                        {errors?.campusActivitySlotsDtos?.[index]?.name && (
                          <small className="text-danger">
                            {errors.campusActivitySlotsDtos[index].name.message}
                          </small>
                        )}
                      </div>

                      <div className="col-md-3">
                        <input
                          type="time"
                          className="form-control"
                          {...register(
                            `campusActivitySlotsDtos.${index}.slotStartTime`,
                            {
                              required: "This field is required.",
                            }
                          )}
                        />
                        {errors?.campusActivitySlotsDtos?.[index]
                          ?.slotStartTime && (
                          <small className="text-danger">
                            {
                              errors.campusActivitySlotsDtos[index]
                                .slotStartTime.message
                            }
                          </small>
                        )}
                      </div>

                      <div className="col-md-3">
                        <input
                          type="time"
                          className="form-control"
                          {...register(
                            `campusActivitySlotsDtos.${index}.slotEndTime`,
                            {
                              required: "This field is required.",
                              validate: (value) => {
                                const startTime = watch(
                                  `campusActivitySlotsDtos.${index}.slotStartTime`
                                );
                                if (!startTime) return true; // no start time yet entered
                                return (
                                  value >= startTime ||
                                  "Must be same or after Start Time."
                                );
                              },
                            }
                          )}
                        />
                        {errors?.campusActivitySlotsDtos?.[index]
                          ?.slotEndTime && (
                          <small className="text-danger">
                            {
                              errors.campusActivitySlotsDtos[index].slotEndTime
                                .message
                            }
                          </small>
                        )}
                      </div>

                      <div className="col-md-2 d-flex align-items-start">
                        <button
                          className="btn btn-outline-primary py-1 px-2 me-1"
                          type="button"
                          onClick={() =>
                            append({
                              id: 0,
                              name: "",
                              slotStartTime: "",
                              slotEndTime: "",
                              activityId: 0,
                            })
                          }
                        >
                          <span className="fas fa-plus"></span>
                        </button>

                        {fields.length > 1 && (
                          <button
                            className="btn btn-outline-danger py-1 px-2"
                            type="button"
                            onClick={() => remove(index)}
                          >
                            <span className="fas fa-minus"></span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-12 mt-2">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="calendar-container h-100">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={fetchEvents}
              eventClick={handleEventClick}
              dayMaxEvents={1}
              height="auto"
              eventDisplay="block"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth",
              }}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                meridiem: true,
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="mb-3 fs-8">Activity Enrollments</h4>
              <DataTable
                columns={columns}
                data={activitiesData}
                pagination
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                dense
                customStyles={customTableStyles}
                highlightOnHover={true}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
          </div>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible && (
        <DropdownWithCustomValuesModal
          dropdownName={customDropdownName}
          onClose={() => {
            setIsCustomDropdownValuesModalVisible(false);
            if (customDropdownName === "activityCategoryId") {
              fetchCommonSettings(
                "activityCategoryId",
                setActivityCategoryData
              );
            } else if (customDropdownName === "activityVenueId") {
              fetchCommonSettings("activityVenueId", setActivityVenueData);
            }
          }}
        />
      )}

      {showModal && selectedEvent && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="mb-0">Activity Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    <strong>Activity:</strong> {selectedEvent.activityName}
                  </p>
                  <p>
                    <strong>Date:</strong> {selectedEvent.date}
                  </p>
                  <p>
                    <strong>Time Slot:</strong> {selectedEvent.time}
                  </p>
                  <p>
                    <strong>Max Capacity:</strong> {selectedEvent.maxCapicity}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showEnrollmentsModal && (
        <EnrollmentsModal
          onClose={() => {
            setShowEnrollmentsModal(false);
            getActivitiesList();
          }}
          enrollments={enrollmentsData}
        />
      )}

      {showPayActivityChallanModal && (
        <PayActivityChallanModal
          title="Pay Activity Challan"
          onClose={() => setShowPayActivityChallanModal(false)}
          payActivityModalData={payRegModalData}
          payActivityChallan={submitPayActivityChallan}
        />
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default ActivityCreationAndFeedbackForm;
